import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import FeedbackService from 'services/FeedbackService';
import UserService from 'services/UserService';
import BaseModal from 'components/BaseModal';
import RatingStars from '../RatingStars';
import ProfileService from 'services/ProfileService';
import Snackbar from 'components/Poster/Snackbar';
import { makeStyles } from '@mui/styles';

export const useStyles = makeStyles((theme) => ({
  formHelperText: {
    textAlign: 'right',
  },
}));

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const initialFormData = {
  user: null,
  score: 0,
  reason: '',
  institution: '',
};

export default function AdminFormModal({ open, onClose }) {
  const classes = useStyles();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [errorFields, setErrorFields] = useState([]);
  const feedbackService = new FeedbackService();
  const profileService = new ProfileService();
  const userService = new UserService();

  useEffect(() => {
    if (formData.user) {
      setFormData({ ...formData, institution: formData.user.profile.institution });
    }
  }, [formData.user]);

  async function fetchUsers(value) {
    if (value) {
      try {
        const { data } = await userService.findAll({ email: value });

        setUsers(data.data);
      } catch (e) {
        console.error(e);
      }
    } else {
      setUsers([]);
    }
  }

  function handleUserInputChange(event, value) {
    fetchUsers(value);
  }

  function handleFieldChange(field, data) {
    if (!!data[field] && errorFields.includes(field)) {
      setErrorFields(errorFields.filter((errorField) => errorField !== field));
    }

    setFormData(data);
  }

  function getErrorFields() {
    const fields = [];

    if (!formData.user) {
      fields.push('user');
    }

    if (formData.reason.length < 2 || formData.reason.length > 255) {
      fields.push('reason');
    }

    return fields;
  }

  function canSubmit() {
    const errors = getErrorFields();

    if (errors.length) {
      setErrorFields(errors);

      return false;
    }

    return true;
  }

  async function handleFeedbackCreate() {
    if (!canSubmit()) {
      return;
    }

    try {
      await feedbackService.create({
        userId: formData.user.id,
        score: formData.score * 2,
        reason: formData.reason,
      });

      if (formData.institution !== formData.user?.profile?.institution) {
        await profileService.update(formData.user.id, {
          institution: formData.institution,
        });
      }

      setSnackbarOpen(true);
      setErrorFields([]);
      onClose();
      setFormData({ ...initialFormData });
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    if (!open) {
      setErrorFields([]);
      setFormData({ ...initialFormData });
    }
  }, [open]);

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        message="Feedback added succesfully!"
        severity="success"
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <BaseModal
        size="sm"
        open={open}
        title={<b>Add a customer review</b>}
        content={
          <Box px={2}>
            <Box mb={3}>
              <Typography align="left">Email</Typography>

              <Autocomplete
                options={users}
                getOptionLabel={(option) =>
                  option.profile.name ? `${option.profile.name} | ${option.email}` : option.email
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options) => options}
                value={formData.user}
                onChange={(event, value) => handleFieldChange('user', { ...formData, user: value })}
                onInputChange={debounce(handleUserInputChange, 500)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth={true}
                    variant="outlined"
                    margin="dense"
                    error={errorFields.includes('user')}
                    helperText={
                      errorFields.includes('user') ? 'Please fill this field correctly' : undefined
                    }
                  />
                )}
              />

              <Box py={1}>
                <Typography align="left">Institution</Typography>

                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={formData.institution || ''}
                  onChange={(event) =>
                    setFormData({ ...formData, institution: event.target.value })
                  }
                />
              </Box>

              <Box>
                <Typography align="left">Message</Typography>

                <TextField
                  fullWidth={true}
                  multiline={true}
                  variant="outlined"
                  margin="dense"
                  error={errorFields.includes('reason')}
                  FormHelperTextProps={{
                    className: !errorFields.includes('reason') ? classes.formHelperText : undefined,
                  }}
                  inputProps={{ minRows: 6 }}
                  value={formData.reason}
                  onChange={({ target: { value } }) =>
                    handleFieldChange('reason', { ...formData, reason: value.slice(0, 255) })
                  }
                />
              </Box>
            </Box>

            <Typography gutterBottom={true}>Evaluation score</Typography>

            <RatingStars
              size="big"
              value={formData.score}
              onChange={(value) => handleFieldChange('score', { ...formData, score: value })}
            />
          </Box>
        }
        actionText="Add"
        cancelButton={false}
        onActionClick={handleFeedbackCreate}
        onClose={onClose}
      />
    </>
  );
}

AdminFormModal.propTypes = Props;
